import {
  CalendarCheck2, CalendarDays, Check, CircleDollarSign, Download,
  FileBarChart, Filter, Heart, Layers3, MessageSquare,
  RefreshCw, Scale, Search, Share2, Sparkles, Trophy,
  Users, X
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  apiClient,
  type DashboardMetric,
  type SocialReportDashboard,
  type SocialReportDashboardFilters,
  type SnapshotFacetFilters,
  type SocialReportDraft,
  type SocialReportSummary
} from "@/shared/api/api-client";
import { AppLink } from "@/shared/components/AppLink";
import { EmptyState } from "@/shared/components/charts/EmptyState";
import { MetricCard } from "@/shared/components/charts/MetricCard";
import { Panel } from "@/shared/components/charts/Panel";
import { RankedBars } from "@/shared/components/charts/RankedBars";
import { useShowsCena } from "@/shared/components/cena-visibility";
import { MultiSelectFilter } from "@/shared/components/MultiSelectFilter";
import { SearchSelect } from "@/shared/components/SearchSelect";
import {
  DASHBOARD_DIMENSIONS,
  SNAPSHOT_CLOSED_CHOICES,
  emptyFacets,
  excludeParam,
  facetOptions,
  facetPayload,
  humanizeLabel,
  metricLabel,
  readFacets,
  type DashboardDimension,
  type DashboardFacet,
  type DashboardFacets
} from "@/shared/filters/snapshot-facets";
import { toMention, type Mention } from "@/shared/types/mention";
import { usePolarityDomain } from "@/shared/types/polarity";
import {
  polarityOptions,
  polarityPalette,
  type PolarityPalette
} from "@/shared/types/polarity";
import {
  formatWindowFromCriteria,
  isoDate,
  localDate
} from "@/shared/utils/date-range";
import { sanitizeMinAdValue } from "@/features/mentions/mention-filter-criteria";
import {
  formatCurrencyCompact,
  formatCurrencyFull
} from "@/shared/utils/format-currency";

import { errorMessage } from "@/shared/api/http";
import { useAppShell } from "@/shell/app-shell-context";
import { canOperate } from "@/shared/utils/roles";
import { ExportFormatDialog, type ExportChoice } from "@/features/exports/ExportFormatDialog";
import { ExportFeedback, useExportRequest } from "@/features/exports/use-export-request";
import { buildTimelineCsv, CSV_BOM } from "./dashboard-csv";
import { useConfirmLeave } from "@/shared/unsaved-changes/UnsavedChanges";
import { formatCena, formatCompact, formatFullDate, formatInteger, formatShortDate, formatSignedCompact } from "@/shared/utils/format";
import { reportBrandNames, summarizeBrands } from "@/features/reports/report-recorte";
import { Select } from "@/shared/components/Select";
import { offersSourceChoice, reportSources, SOURCE_VIEWS, sourceFacetFor, sourceViewOf, visibleSources, type SourceView } from "./v1/dashboard-v1";
import { NOT_IN_THIS_PHOTO, OverviewTab } from "./v1/OverviewTab";
import { RankingsTab } from "./v1/RankingsTab";
import { ThemesTab } from "./v1/ThemesTab";
/** As abas que sobraram (2026-10-25): Diário, Mensal, Semanal e Redes
 * saíram a pedido do Alexsander — o que elas mostravam está na Visão
 * Geral do v1. Link antigo com `?tab=daily` cai na Visão Geral. */
const DASHBOARD_TABS = ["overview", "themes", "rankings"] as const;
type DashboardTab = (typeof DASHBOARD_TABS)[number];

function tabFromParam(raw: string | null): DashboardTab {
  return DASHBOARD_TABS.find(tab => tab === raw) ?? "overview";
}

/** Qual relatório está aberto. O id vai na URL (`?report=` / `?draft=`),
 * então o F5 mantém o recorte e o link é compartilhável. */
type ReportRef = { kind: "report" | "draft"; id: number };

const dashboardSections = [
  { value: "overview", label: "Visão Geral", icon: Layers3 },
  { value: "themes", label: "Análise de Temas", icon: Search },
  { value: "rankings", label: "Rankings", icon: Trophy }
] as const;

const weekDayOptions = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Segunda" },
  { value: 2, label: "Terça" },
  { value: 3, label: "Quarta" },
  { value: 4, label: "Quinta" },
  { value: 5, label: "Sexta" },
  { value: 6, label: "Sábado" }
];

/** A cor do selo de polaridade dos destaques — as QUATRO da paleta da marca.
 *
 *  Tenta primeiro o domínio, pela chave de wire (`POSITIVO`…), que é o
 *  caminho certo. O farejamento de texto sobrevive só como reserva pro
 *  dado antigo, cuja chave congelada não bate com o domínio de hoje
 *  (`favoravel` minúsculo) — sem ele essas fatias perderiam a cor que
 *  já tinham.
 *
 *  A ordem dos testes importa: "desfavorável" CONTÉM "favor", então
 *  precisa ser decidido antes. Era o mesmo cuidado de antes, agora com
 *  quatro destinos em vez de dois. */
const paletteFor = (
  key: string,
  label?: string
): PolarityPalette | "unclassified" => {
  const fromDomain = polarityPalette(key);
  if (fromDomain !== "unclassified") return fromDomain;

  const value = (label || key).toLocaleLowerCase("pt-BR");
  if (value.includes("desfavor")) return "unfavorable";
  if (value.includes("negat")) return "negative";
  if (value.includes("favor")) return "favorable";
  if (value.includes("posit")) return "positive";
  if (value.includes("neutr")) return "neutral";
  return "unclassified";
};

function percentChange(current: number, previous: number | null): number | null {
  if (previous === null || !previous) return null;
  return ((current - previous) / previous) * 100;
}

const rankedBarsMetricNoun: Record<"count" | "reach" | "interactions", string> = {
  count: "menções",
  reach: "de alcance",
  interactions: "interações"
};

/** Idem pro ranking: o `metric` decide qual coluna do `DashboardMetric`
 *  vira a barra (hoje só `count` é usado — o seletor de métrica é a
 *  pendência mais barata da lista da Gi; ver `v1/PolarityPanel.tsx`). */
function DashboardRankedBars({ items, metric = "count", limit }: { items: DashboardMetric[]; metric?: "count" | "reach" | "interactions"; limit?: number }) {
  return (
    <RankedBars
      items={items.map(item => ({ key: item.key, label: metricLabel(item), value: item[metric] }))}
      noun={rankedBarsMetricNoun[metric]}
      limit={limit}
    />
  );
}

function BreakdownTable({ items }: { items: DashboardMetric[] }) {
  const showsCena = useShowsCena();
  if (!items.length) return <EmptyState/>;
  return (
    <div className="dashboard-table-wrap">
      <table className="dashboard-data-table">
        <thead><tr><th>Dimensão</th><th>Menções</th><th>Alcance</th><th>Interações</th><th>Valoração</th>{showsCena && <th>CENA</th>}</tr></thead>
        <tbody>{items.map(item => <tr key={item.key}><td>{metricLabel(item)}</td><td>{formatInteger(item.count)}</td><td>{formatCompact(item.reach)}</td><td>{formatCompact(item.interactions)}</td><td title={formatCurrencyFull(item.total_ad_value)}>{formatCurrencyCompact(item.total_ad_value)}</td>{showsCena && <td>{formatCena(item.average_cena)}</td>}</tr>)}</tbody>
      </table>
    </div>
  );
}

/**
 * O dia que veio na URL, ou vazio.
 *
 * Existe por causa dos links gerados ANTES do conserto de 2026-08-27:
 * os atalhos de período gravavam `?from=` com o instante cru da janela
 * (`2026-03-01T00:00:00+00:00`) ou, quando a conta estourava, com
 * `NaN-NaN-NaN`. Os dois envenenam o mesmo estado: o
 * `<input type="date">` recusa em silêncio (campo vazio, sem erro) e o
 * servidor, que tipa `date_from` como `date`, responde 422. Um link
 * velho colado num chat continuaria quebrado depois do fix se ninguém
 * saneasse aqui.
 *
 * **O dia sai da string, nunca de conversão de fuso.** É a mesma regra
 * que `formatWindowFromCriteria` aplica às linhas anteriores à
 * migração: naquele instante a hora foi inventada (meia-noite UTC), e
 * só o DIA é afirmação verdadeira. Converter pro fuso local recuaria
 * `2026-07-01T00:00:00+00:00` para 30 de junho aqui em Brasília — o
 * mesmo "28 de fev." que esta entrega está consertando, reintroduzido
 * pelo próprio conserto.
 */
const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function dayFromParam(raw: string | null): string {
  if (!raw) return "";
  const day = raw.slice(0, 10);
  if (!DAY_ONLY.test(day)) return "";
  // Um dia bem-formado ainda pode não existir ("2026-02-31"): o
  // construtor normaliza em silêncio, então a ida-e-volta é quem
  // responde.
  return isoDate(localDate(day)) === day ? day : "";
}

/** Rótulo do relatório no select. Desde 2026-08-26 o relatório salvo
 * TEM nome, e é ele que identifica; os salvos antes disso caem no
 * rótulo derivado (janela + marca + data), que era a identidade de
 * então. Rascunho continua sem nome por desenho — ele é passageiro. */
/**
 * As marcas do relatório, pelos RÓTULOS CONGELADOS.
 *
 * A coluna `brand_name` só responde no caso singular — ela vem do join
 * com `brand_id`, que é `null` num relatório de duas marcas. Sem esta
 * função, todo relatório multi-marca apareceria na lista sem marca
 * nenhuma, indistinguível de um relatório da empresa inteira.
 *
 * Os nomes saem dos rótulos congelados, não do catálogo vivo: renomear
 * uma marca não pode reescrever o rótulo de um relatório salvo. Sem
 * rótulo (v1/v2, ou marca sumida), cai na coluna.
 */
function brandLabel(item: SocialReportSummary | SocialReportDraft): string {
  // A resolução e o truncamento moram em `report-recorte.ts`: a tela de
  // relatório fazia a MESMA coisa cortando em três, pro mesmo
  // relatório.
  const nomes = reportBrandNames(item);
  return nomes.length > 0 ? ` · ${summarizeBrands(nomes, "")}` : "";
}

function reportOptionLabel(item: SocialReportSummary | SocialReportDraft): string {
  // Pela CRITERIA, não pelas colunas: a janela virou instante em
  // 2026-08-27, e nas linhas anteriores à migração a hora foi inventada
  // pelo backfill — convertida pro fuso local, `2026-03-01T00:00:00Z`
  // vira "28 de fev." aqui em Brasília. `formatWindowFromCriteria` sabe
  // que da versão 1 só o DIA é afirmação verdadeira.
  const window = formatWindowFromCriteria(
    item.generation_criteria,
    item.window_start,
    item.window_end
  );
  const brand = brandLabel(item);
  if ("saved_at" in item) {
    const saved = `salvo em ${formatShortDate(new Date(item.saved_at))}`;
    // O acesso a `name` fica DENTRO do estreitamento: rascunho não tem
    // o campo, e o TypeScript recusaria fora daqui.
    return item.name ? `${item.name} · ${window}${brand}` : `${window}${brand} · ${saved}`;
  }
  const remaining = Math.max(0, Math.ceil((new Date(item.expires_at).getTime() - Date.now()) / 86_400_000));
  return `${window}${brand} · expira em ${remaining} ${remaining === 1 ? "dia" : "dias"}`;
}

export function GlobalDashboardPage({ token, companyId, openMention }: {
  token: string;
  companyId: string;
  openMention: (mention: Mention) => void;
}) {
  const navigate = useNavigate();
  const showsCena = useShowsCena();
  // Visualizador só aplica as planilhas salvas; salvar e apagar é do Técnico.
  const { profile } = useAppShell();
  const canManagePresets = profile ? canOperate(profile) : true;
  // No React Router o hook devolve uma tupla; só a leitura interessa aqui,
  // porque a escrita passa pelo `navigate` mais abaixo junto com
  // `preventScrollReset`.
  const [searchParams] = useSearchParams();
  const initialRef: ReportRef | null = searchParams.get("report")
    ? { kind: "report", id: Number(searchParams.get("report")) }
    : searchParams.get("draft")
      ? { kind: "draft", id: Number(searchParams.get("draft")) }
      : null;

  const [tab, setTab] = useState<DashboardTab>(tabFromParam(searchParams.get("tab")));
  // Trocar de aba desmonta a anterior (os textos da Análise de Temas):
  // com alteração não salva, pergunta antes.
  const confirmLeave = useConfirmLeave();
  const [selection, setSelection] = useState<ReportRef | null>(initialRef);
  const [reports, setReports] = useState<SocialReportSummary[]>([]);
  const [drafts, setDrafts] = useState<SocialReportDraft[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  // Um estado só pras onze dimensões — ver `DASHBOARD_DIMENSIONS`.
  const [facets, setFacets] = useState<DashboardFacets>(() =>
    readFacets(searchParams)
  );
  // Único filtro NUMÉRICO do dashboard, e o único que não é faceta.
  // String no estado porque é o que está digitado no input; o
  // saneamento pra inteiro >= 1 acontece no envio, pela MESMA função
  // que a tela de menções usa — se as duas divergissem, o mesmo corte
  // daria listas diferentes.
  const [minAdValue, setMinAdValue] = useState(
    searchParams.get("minAdValue") ?? ""
  );
  const [granularity, setGranularity] = useState<"day" | "week" | "month">((searchParams.get("group") as "day" | "week" | "month" | null) ?? "week");
  const [weekStart, setWeekStart] = useState(Number(searchParams.get("weekStart") ?? 1));
  const [dateFrom, setDateFrom] = useState(dayFromParam(searchParams.get("from")));
  const [dateTo, setDateTo] = useState(dayFromParam(searchParams.get("to")));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [data, setData] = useState<SocialReportDashboard | null>(null);
  // As cores e as fatias por sentimento dependem do domínio de polaridade.
  usePolarityDomain(data);
  const [filterCatalog, setFilterCatalog] = useState({
    channels: [] as DashboardMetric[],
    approaches: [] as DashboardMetric[],
    formats: [] as DashboardMetric[],
    tiers: [] as DashboardMetric[],
    influencerTypes: [] as DashboardMetric[],
    publisherGroups: [] as DashboardMetric[],
    themeGroups: [] as DashboardMetric[],
    themes: [] as DashboardMetric[],
    brands: [] as DashboardMetric[],
    // `null`, e não `[]`, é o inicial de propósito: `[]` diria
    // "a dimensão existe e está vazia" antes da primeira resposta —
    // exatamente a ambiguidade que a flag do servidor existe pra
    // matar. Só a resposta decide se este relatório tem porta-voz.
    spokespersons: null as DashboardMetric[] | null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A planilha vai pra fila (2026-10-15); ver `useExportRequest`.
  const exporter = useExportRequest({
    token,
    onOpenDownloads: () => navigate(`/empresas/${companyId}/downloads`)
  });
  const [exportDialog, setExportDialog] = useState(false);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const filterCloseRef = useRef<HTMLButtonElement>(null);

  /** A janela do relatório aberto — limita os inputs de data e define os
   * atalhos de período (que aqui são relativos à FOTO, nunca a hoje). */
  const window_ = useMemo(() => {
    if (!selection) return null;
    const found = selection.kind === "report"
      ? reports.find(item => item.id === selection.id)
      : drafts.find(item => item.id === selection.id);
    if (!found) return null;
    // DOIS pares, e a distinção não é cosmética. A janela do relatório
    // é um INSTANTE desde 2026-08-27 (`2026-03-01T00:00:00Z`), mas esta
    // tela vive no domínio do DIA: os `<input type="date">` abaixo
    // RECUSAM valor com hora (o atributo vira inválido e é ignorado em
    // silêncio) e os params `date_from`/`date_to` são `date` no
    // servidor. Guardar só o instante fazia a concatenação manual de
    // `T12:00:00` produzir `Invalid Date` — o `RangeError` que derrubava
    // a tela inteira.
    const start = localDate(found.window_start);
    const end = localDate(found.window_end);
    return {
      start,
      end,
      startDay: isoDate(start),
      endDay: isoDate(end)
    };
  }, [selection, reports, drafts]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const [saved, pending] = await Promise.all([
        apiClient.listSocialReports(token, { size: 50 }),
        // Rascunho é de Técnico ou acima: o Visualizador (2026-10-22) toma
        // 403 aqui e fica só com os salvos, em vez de perder a tela inteira.
        apiClient.listSocialReportDrafts(token).catch(() => [])
      ]);
      setReports(saved.items);
      setDrafts(pending);
      setSelection(current => {
        if (current) return current;
        if (saved.items.length) return { kind: "report", id: saved.items[0].id };
        if (pending.length) return { kind: "draft", id: pending[0].id };
        return null;
      });
    } catch (err) {
      setError(errorMessage(err, "Não foi possível listar as amostras."));
    } finally {
      setCatalogLoading(false);
    }
  }, [token]);

  useEffect(() => { void loadCatalog(); }, [loadCatalog]);

  // O recorte de LEITURA do dashboard: tudo o que a pessoa vê filtrado,
  // menos o desenho do gráfico (granularidade e início da semana). É a
  // fonte única do `load` e da exportação da planilha — o que está na
  // tela é o que sai no arquivo, e os dois não têm por onde divergir.
  // Os parâmetros existem pra pré-busca do seletor de fonte perguntar
  // pelo recorte de DEPOIS do clique com a mesma montagem do `load` — a
  // chave do cache tem que sair idêntica.
  const readFilters = (
    withFacets: DashboardFacets = facets,
    withMinAdValue: string = minAdValue
  ): SnapshotFacetFilters => ({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    ...facetPayload(withFacets),
    minAdValue: sanitizeMinAdValue(withMinAdValue) ?? undefined
  });

  /** As respostas recentes, por recorte (2026-10-30). A foto é
   * congelada, então a mesma pergunta tem a mesma resposta: voltar de
   * "Redes" pra "Tudo" não vai ao servidor (~1 s num relatório de 35 mil
   * menções). Guarda a PROMESSA, então a pré-busca e o clique que chega
   * no meio dela dividem a mesma ida. "Atualizar" passa por cima. */
  const responsesRef = useRef(new Map<string, Promise<SocialReportDashboard>>());
  const fetchDashboard = useCallback((
    filters: SocialReportDashboardFilters,
    force = false
  ): Promise<SocialReportDashboard> | null => {
    if (!selection) return null;
    const cache = responsesRef.current;
    const key = JSON.stringify([selection.kind, selection.id, filters]);
    const cached = force ? undefined : cache.get(key);
    if (cached) return cached;
    const pending = apiClient.socialReportDashboard(
      token,
      selection.kind === "report" ? { reportId: selection.id } : { draftId: selection.id },
      filters
    );
    cache.delete(key);
    cache.set(key, pending);
    // Falha não fica guardada: a próxima tentativa vai ao servidor.
    pending.catch(() => { if (cache.get(key) === pending) cache.delete(key); });
    // A ordem de inserção do `Map` é a idade: sai a mais velha.
    while (cache.size > 12) cache.delete(cache.keys().next().value as string);
    return pending;
  }, [token, selection]);

  // Cada `load` ganha um número; só o mais recente escreve na tela. Sem
  // isso, dois cliques rápidos podiam terminar com a resposta do
  // PRIMEIRO por último — o seletor dizendo "Tudo" e a tela em "Redes".
  const loadSeq = useRef(0);
  const load = useCallback(async (force = false) => {
    if (!selection) return;
    const seq = ++loadSeq.current;
    const pending = fetchDashboard({ ...readFilters(), granularity, weekStart }, force);
    if (!pending) return;
    setLoading(true);
    setError(null);
    try {
      const result = await pending;
      if (seq !== loadSeq.current) return;
      setData(result);
      // O catálogo de opções é do relatório ABERTO — acumular entre
      // relatórios ofereceria filtro de canal que não existe neste.
      setFilterCatalog({
        channels: result.channels,
        approaches: result.approaches,
        formats: result.formats,
        tiers: result.tiers,
        influencerTypes: result.influencer_types,
        publisherGroups: result.publisher_groups,
        themeGroups: result.theme_groups,
        themes: result.themes,
        brands: result.brands,
        spokespersons: result.spokespersons
      });
    } catch (err) {
      if (seq !== loadSeq.current) return;
      const message = errorMessage(err, "Não foi possível carregar o dashboard.");
      // 404 num rascunho tem três causas indistinguíveis daqui (expirou,
      // foi salvo, foi descartado) — a mensagem não escolhe uma.
      setError(
        selection.kind === "draft" && /não encontrad/i.test(message)
          ? "Este rascunho não está mais disponível — pode ter expirado, sido salvo ou descartado."
          : message
      );
      setData(null);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
    // Sem `tab`: trocar de aba não muda o recorte, e buscava o dashboard
    // inteiro de novo a cada clique.
  }, [
    fetchDashboard, selection, dateFrom, dateTo, granularity, weekStart,
    facets, minAdValue
  ]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!filtersOpen) return;
    filterCloseRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
        filterTriggerRef.current?.focus();
        return;
      }
      if (event.key === "Tab") {
        const drawer = document.getElementById("dashboard-filter-drawer");
        const focusable = Array.from(drawer?.querySelectorAll<HTMLElement>("button:not(:disabled), summary, input:not(:disabled), select:not(:disabled)") ?? []);
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [filtersOpen]);

  useEffect(() => {
    const query = new URLSearchParams();
    if (selection?.kind === "report") query.set("report", String(selection.id));
    if (selection?.kind === "draft") query.set("draft", String(selection.id));
    if (tab !== "overview") query.set("tab", tab);
    DASHBOARD_DIMENSIONS.forEach(({ key }) => {
      facets[key].values.forEach(value => query.append(key, value));
      if (facets[key].exclude) query.set(excludeParam(key), "1");
    });
    // O valor CRU vai pra URL (não o saneado): o input continua
    // mostrando o que a pessoa digitou depois de um F5, e quem sabe o
    // que vale é o `load`.
    if (minAdValue) query.set("minAdValue", minAdValue);
    if (granularity !== "week") query.set("group", granularity);
    if (weekStart !== 1) query.set("weekStart", String(weekStart));
    if (dateFrom) query.set("from", dateFrom);
    if (dateTo) query.set("to", dateTo);
    navigate(`/empresas/${companyId}/dashboard${query.size ? `?${query}` : ""}`, {
      replace: true,
      preventScrollReset: true
    });
  }, [
    companyId, selection, tab, granularity, weekStart, dateFrom, dateTo, navigate,
    facets, minAdValue
  ]);

  const selectReport = (value: string) => {
    const [kind, id] = value.split(":");
    setSelection(value ? { kind: kind as "report" | "draft", id: Number(id) } : null);
    // Recorte de data e catálogo são do relatório anterior — não valem
    // pro próximo, cujas datas podem nem existir nesta janela.
    setDateFrom("");
    setDateTo("");
  };
  /** Os atalhos de período são relativos à JANELA do relatório — presets
   * relativos a hoje ("últimos 7 dias") dariam recorte vazio numa foto
   * de julho. */
  const windowShortcut = (kind: "all" | "first" | "second" | "last7") => {
    if (!window_) return;
    const { start, end } = window_;
    if (kind === "all") { setDateFrom(""); setDateTo(""); return; }
    if (kind === "last7") {
      const from = new Date(end);
      from.setDate(from.getDate() - 6);
      setDateFrom(isoDate(from < start ? start : from));
      setDateTo(window_.endDay);
      return;
    }
    const middle = new Date((start.getTime() + end.getTime()) / 2);
    if (kind === "first") { setDateFrom(window_.startDay); setDateTo(isoDate(middle)); }
    else { setDateFrom(isoDate(middle)); setDateTo(window_.endDay); }
  };
  /** Marca/desmarca um valor de uma dimensão. */
  const toggleFacet = (key: DashboardDimension, value: string) => {
    setFacets(current => {
      const values = new Set(current[key].values);
      if (values.has(value)) values.delete(value);
      else values.add(value);
      return { ...current, [key]: { ...current[key], values } };
    });
  };

  /** Liga/desliga o modo "exceto" de uma dimensão. */
  const setFacetExclude = (key: DashboardDimension, exclude: boolean) => {
    setFacets(current => ({ ...current, [key]: { ...current[key], exclude } }));
  };

  /** Limpa a seleção de uma dimensão, preservando o modo. */
  const clearFacet = (key: DashboardDimension) => {
    setFacets(current => ({
      ...current,
      [key]: { ...current[key], values: new Set() }
    }));
  };

  // Imprensa, redes ou as duas (2026-10-30) — a faceta `sources` lida
  // como seletor; ver `sourceViewOf`.
  const sourceView = sourceViewOf(facets.sources);
  /** Troca a fonte. Os filtros de meio-escopo da outra fonte saem
   *  junto: a gaveta esconde o grupo dela, e um filtro que ninguém vê
   *  não pode continuar contando no selo. */
  const withSource = (current: DashboardFacets, view: SourceView): DashboardFacets => {
    const cleared = (facet: DashboardFacet): DashboardFacet => ({ ...facet, values: new Set() });
    return {
      ...current,
      sources: sourceFacetFor(view),
      ...(view === "network" ? { tiers: cleared(current.tiers), formats: cleared(current.formats), mediaTypes: cleared(current.mediaTypes) } : {}),
      ...(view === "press" ? { influencerTypes: cleared(current.influencerTypes), publisherGroups: cleared(current.publisherGroups) } : {})
    };
  };
  const chooseSource = (view: SourceView) => {
    setFacets(current => withSource(current, view));
    if (view === "network") setMinAdValue("");
  };
  /** Começa a buscar o recorte de uma fonte quando o ponteiro (ou o
   *  foco) chega no botão — o clique, ~300 ms depois, encontra a ida já
   *  andando no cache em vez de começar do zero. */
  const prefetchSource = (view: SourceView) => {
    if (view === sourceView) return;
    void fetchDashboard({
      ...readFilters(withSource(facets, view), view === "network" ? "" : minAdValue),
      granularity,
      weekStart
    })?.catch(() => undefined);
  };

  const resetFilters = () => {
    setGranularity("week"); setWeekStart(1);
    // A fonte fica: ela tem controle próprio à vista, fora da gaveta, e
    // "Limpar filtros" não pode desfazer o que a gaveta não mostra.
    setFacets(current => ({ ...emptyFacets(), sources: current.sources }));
    setMinAdValue("");
    setDateFrom(""); setDateTo("");
  };
  // Sem a fonte, pelo mesmo motivo: o selo conta o que está ESCONDIDO na
  // gaveta, e a fonte já aparece escolhida ao lado das abas.
  const activeFilterCount = [
    dateFrom || dateTo,
    ...DASHBOARD_DIMENSIONS.filter(({ key }) => key !== "sources").flatMap(({ key }) => [...facets[key].values]),
    // Saneado, não cru: "0" digitado não é filtro (o servidor recusa),
    // e contá-lo acenderia o badge sem nada ter mudado na tela.
    sanitizeMinAdValue(minAdValue) !== null
  ].filter(Boolean).length;
  const change = data ? percentChange(data.total_mentions, data.previous_mentions) : null;
  const sources = data ? visibleSources(data, sourceView) : { press: true, network: true };
  // O que o RELATÓRIO tem, não o recorte (2026-10-30): relatório só de
  // redes não oferece "Imprensa" no seletor nem o grupo dela na gaveta.
  const reportHas = data ? reportSources(data) : { press: true, network: true };
  const sourceChoice = data ? offersSourceChoice(data) : true;
  // Fonte escolhida que este relatório não tem — link antigo, ou a troca
  // de relatório herdou a escolha do anterior: volta pra "Tudo" em vez
  // de mostrar a tela vazia com o seletor escondido.
  useEffect(() => {
    if (!data || sourceView === "all" || reportSources(data)[sourceView]) return;
    setFacets(current => ({ ...current, sources: sourceFacetFor("all") }));
  }, [data, sourceView]);
  const selectedPeriod = dateFrom && dateTo
    ? `${formatFullDate(localDate(dateFrom))} — ${formatFullDate(localDate(dateTo))}`
    : window_
      ? `${formatFullDate(window_.start)} — ${formatFullDate(window_.end)}`
      : "Nenhuma amostra selecionada";
  const selectedReportLabel = selection
    ? (selection.kind === "report"
      ? reports.find(item => item.id === selection.id)
      : drafts.find(item => item.id === selection.id))
    : null;
  // Do domínio da instalação (`/api/polarities`).
  const sentimentOptions = polarityOptions().map(option => ({
    value: option.name,
    label: option.label
  }));
  /** As opções de uma dimensão que vem do catálogo do relatório. */
  const optionsFor = (key: DashboardDimension) => {
    const dimension = DASHBOARD_DIMENSIONS.find(item => item.key === key);
    const catalog = dimension?.catalog;
    return facetOptions(
      facets[key].values,
      catalog ? filterCatalog[catalog] : null,
      SNAPSHOT_CLOSED_CHOICES[key]
    );
  };
  // A dimensão existe nesta foto? `null` é resposta do servidor, não
  // ausência de dado carregado — ver o estado inicial do catálogo.
  const hasSpokespersons = filterCatalog.spokespersons !== null;

  const exportCsv = () => {
    if (!data) return;
    // Dialeto Excel pt-BR (`;` + vírgula decimal) — ver `dashboard-csv.ts`.
    // Número cru com ponto virava `6.208.571.428.571.420` na planilha.
    const csv = buildTimelineCsv(data.timeline, showsCena);
    const url = URL.createObjectURL(new Blob([`${CSV_BOM}${csv}`], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a"); anchor.href = url; anchor.download = `dashboard-reputaition-${isoDate(new Date())}.csv`; anchor.click(); URL.revokeObjectURL(url);
  };

  // A MESMA planilha da tela de relatório (layout do `export_cena`), com o
  // recorte de leitura do dashboard. Amostra salva ou rascunho (desde
  // 2026-11-03). O arquivo é gerado em segundo plano e entregue em
  // Downloads; falha de ação não troca a página (`exporter.error`).
  const exportSpreadsheet = async (choice: ExportChoice) => {
    if (!data || !selection) return;
    const ok = await exporter.request(
      {
        kind: selection.kind === "report" ? "export_report" : "export_draft",
        subject_id: selection.id,
        format: choice.format,
        ...(choice.columns ? { columns: choice.columns } : {}),
        header: choice.header,
        theme_layout: choice.theme_layout,
        highlight_only: choice.highlight_only
      },
      readFilters()
    );
    if (ok) setExportDialog(false);
  };

  const openTopMention = async (mentionId: number) => {
    try {
      openMention(toMention(await apiClient.mentionDetail(token, mentionId)));
    } catch {
      // Silencioso de propósito: o drawer é atalho, e o erro dele não
      // deve derrubar o dashboard inteiro.
    }
  };

  const mentionsRanking = (limit: number) => (
    data && data.top_mentions.length ? (
      <ol className="dashboard-mentions-ranking">
        {data.top_mentions.slice(0, limit).map((mention, index) => (
          <li key={mention.mention_id}>
            <span className="dashboard-rank-number">{String(index + 1).padStart(2, "0")}</span>
            <div>
              <button
                type="button"
                className={`text-button${mention.title ? "" : " is-excerpt"}`}
                onClick={() => void openTopMention(mention.mention_id)}
              >
                {mention.title || mention.excerpt || "Publicação sem título"}
              </button>
              <span>{mention.channel ?? humanizeLabel(mention.source ?? "")} · {formatShortDate(new Date(mention.published_at))}</span>
            </div>
            {mention.polarity && <span className={`badge ${paletteFor(mention.polarity)}`}>{humanizeLabel(mention.polarity)}</span>}
            <em><Share2 size={13} aria-hidden="true"/>{formatCompact(mention.interactions)}</em>
          </li>
        ))}
      </ol>
    ) : <EmptyState/>
  );

  if (!catalogLoading && !selection) {
    return (
      <main className="dashboard-page">
        <div className="operational-state">
          <span>
            O dashboard mostra o recorte de uma amostra. Gere a primeira para
            começar a acompanhar volume, polaridade e temas.
          </span>
          <AppLink className="primary-button" to={`/empresas/${companyId}/relatorios/cena`}>
            <FileBarChart size={15} aria-hidden="true"/>Fechar amostra
          </AppLink>
        </div>
      </main>
    );
  }

  if (error) return (
    <div className="operational-state error" role="alert">
      <span>{error}</span>
      <button type="button" className="secondary-button" onClick={() => { void loadCatalog(); void load(true); }}>Tentar Novamente</button>
    </div>
  );

  return (
    <main className="dashboard-page" aria-busy={loading}>
      <header className="dashboard-hero">
        <div>
          <span className="dashboard-eyebrow"><Sparkles size={14} aria-hidden="true"/>Inteligência reputacional</span>
          <h1 className="dashboard-page-title">Panorama de reputação</h1>
          <p>Recorte congelado da amostra selecionada — marcas, canais e temas monitorados.</p>
        </div>
        <div className="dashboard-hero-actions">
          <button type="button" className="secondary-button" onClick={exportCsv} disabled={!data}><Download size={15} aria-hidden="true"/>Exportar CSV</button>
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              exporter.clearError();
              setExportDialog(true);
            }}
            disabled={!data || !selection || exporter.busy}
          >
            <Download size={15} aria-hidden="true"/>{exporter.busy ? "Gerando…" : "Exportar planilha"}
          </button>
          <button type="button" className="primary-button" onClick={() => window.print()} disabled={!data}><FileBarChart size={15} aria-hidden="true"/>Gerar Relatório</button>
        </div>
      </header>
      <ExportFeedback exporter={exporter} suppressError={exportDialog} />
      {exportDialog && (
        <ExportFormatDialog
          token={token}
          columns
          canManagePresets={canManagePresets}
          totalRows={data?.total_mentions ?? null}
          busy={exporter.busy}
          error={exporter.error}
          onConfirm={choice => void exportSpreadsheet(choice)}
          onCancel={() => setExportDialog(false)}
        />
      )}

      {/* A fonte mora ao lado das abas, fora da barra de recorte (que
          não tem largura pra mais um controle): as duas respondem "o que
          estou vendo", e a fonte some/mostra bloco inteiro como a aba. */}
      <div className="dashboard-view-row">
        <nav className="dashboard-section-nav" aria-label="Relatórios do dashboard">
          {dashboardSections.map(section => {
            const Icon = section.icon;
            const active = tab === section.value;
            return (
              <button type="button" key={section.value} className={active ? "active" : ""} aria-pressed={active} onClick={() => confirmLeave(() => setTab(section.value))}>
                <Icon size={16} aria-hidden="true"/>
                <span>{section.label}</span>
              </button>
            );
          })}
        </nav>
        {sourceChoice && <div className="dashboard-source-switch" role="group" aria-label="Fonte dos dados">
          <span className="dashboard-source-switch-label" aria-hidden="true">Fonte</span>
          {SOURCE_VIEWS.map(option => (
            <button
              type="button"
              key={option.value}
              className={sourceView === option.value ? "active" : ""}
              aria-pressed={sourceView === option.value}
              onClick={() => chooseSource(option.value)}
              onPointerEnter={() => prefetchSource(option.value)}
              onFocus={() => prefetchSource(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>}
      </div>

      <div className="dashboard-scope-bar">
        <div className="dashboard-scope-summary" aria-label="Recorte atual do dashboard">
          <span><FileBarChart size={15} aria-hidden="true"/>{selectedReportLabel ? reportOptionLabel(selectedReportLabel) : "Amostra"}</span>
          <span><CalendarDays size={15} aria-hidden="true"/>{selectedPeriod}</span>
          {activeFilterCount > 0 && <strong>{activeFilterCount} {activeFilterCount === 1 ? "filtro adicional" : "filtros adicionais"}</strong>}
        </div>
        <div className="dashboard-scope-actions">
          {/* A lista inteira já vem (são os relatórios DESTA empresa),
              então a busca é local — o que a caixa resolve é achar um
              relatório pelo nome quando a empresa tem dezenas. */}
          <div className="dashboard-report-select">
            <SearchSelect
              label="Amostra"
              required
              allLabel="Nenhum"
              value={selection ? `${selection.kind}:${selection.id}` : ""}
              options={[
                ...reports.map(item => ({ value: `report:${item.id}`, label: reportOptionLabel(item), group: "Salvos" })),
                ...drafts.map(item => ({ value: `draft:${item.id}`, label: reportOptionLabel(item), group: "Rascunhos" }))
              ]}
              onChange={selectReport}
            />
          </div>
          <button type="button" className="dashboard-refresh" onClick={() => void load(true)} aria-label="Atualizar dashboard"><RefreshCw size={15} className={loading ? "spin" : ""} aria-hidden="true"/><span aria-live="polite">{loading ? "Atualizando…" : "Atualizar"}</span></button>
          <button ref={filterTriggerRef} type="button" className="primary-button dashboard-filter-trigger" aria-haspopup="dialog" aria-expanded={filtersOpen} aria-controls="dashboard-filter-drawer" onClick={() => setFiltersOpen(true)}><Filter size={16} aria-hidden="true"/>Filtrar{activeFilterCount > 0 && <span>{activeFilterCount}</span>}</button>
        </div>
      </div>

      {filtersOpen && (
        <div className="dashboard-filter-layer">
          <button type="button" tabIndex={-1} className="dashboard-filter-backdrop" aria-label="Fechar filtros" onClick={() => { setFiltersOpen(false); filterTriggerRef.current?.focus(); }}/>
          <section id="dashboard-filter-drawer" className="dashboard-filter-panel" role="dialog" aria-modal="true" aria-labelledby="dashboard-filter-title">
            <header className="drawer-head">
              <div>
                <span>DASHBOARD</span>
                <strong id="dashboard-filter-title">Filtros do dashboard</strong>
              </div>
              <button ref={filterCloseRef} type="button" className="icon-button" aria-label="Fechar filtros" onClick={() => { setFiltersOpen(false); filterTriggerRef.current?.focus(); }}><X size={18} aria-hidden="true"/></button>
            </header>

            <div className="drawer-body">
              <div className="filter-group">
                <h4 className="filter-group-title">Recorte principal</h4>
                <div className="filter-section">
                  <div className="filter-section-head"><Layers3 size={15} aria-hidden="true"/><span>Agrupar por</span></div>
                  <Select name="dashboard-granularity" value={granularity} onChange={event => setGranularity(event.target.value as "day" | "week" | "month")}><option value="day">Dia</option><option value="week">Semana</option><option value="month">Mês</option></Select>
                </div>
                <div className="filter-section">
                  <div className="filter-section-head"><CalendarCheck2 size={15} aria-hidden="true"/><span>Semana começa em</span></div>
                  <Select name="dashboard-week-start" value={weekStart} onChange={event => setWeekStart(Number(event.target.value))}>
                    {weekDayOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </Select>
                </div>
                <div className="filter-section">
                  <div className="filter-section-head"><CalendarDays size={15} aria-hidden="true"/><span>Período dentro da amostra</span></div>
                  <div className="dashboard-quick-ranges" role="group" aria-label="Recortes da janela da amostra">
                    <button type="button" className={!dateFrom && !dateTo ? "active" : ""} aria-pressed={!dateFrom && !dateTo} onClick={() => windowShortcut("all")}>Janela toda</button>
                    <button type="button" onClick={() => windowShortcut("first")}>1ª metade</button>
                    <button type="button" onClick={() => windowShortcut("second")}>2ª metade</button>
                    <button type="button" onClick={() => windowShortcut("last7")}>Últimos 7 dias</button>
                  </div>
                  <span className="filter-subtle-label">Início</span>
                  <input type="date" name="dashboard-date-from" aria-label="Data inicial" value={dateFrom} min={window_?.startDay} max={window_?.endDay} autoComplete="off" onChange={event => setDateFrom(event.target.value)}/>
                  <span className="filter-subtle-label">Fim</span>
                  <input type="date" name="dashboard-date-to" aria-label="Data final" value={dateTo} min={window_?.startDay} max={window_?.endDay} autoComplete="off" onChange={event => setDateTo(event.target.value)}/>
                </div>
              </div>

              <div className="filter-group">
                <h4 className="filter-group-title">Geral</h4>
                <div className="dashboard-advanced-filters">
                  <MultiSelectFilter label="Sentimento" options={sentimentOptions} selected={facets.sentiments.values} exclude={facets.sentiments.exclude} onExcludeChange={value => setFacetExclude("sentiments", value)} onToggle={value => toggleFacet("sentiments", value)} onClear={() => clearFacet("sentiments")}/>
                  <MultiSelectFilter label="Canal" options={optionsFor("channels")} selected={facets.channels.values} exclude={facets.channels.exclude} onExcludeChange={value => setFacetExclude("channels", value)} onToggle={value => toggleFacet("channels", value)} onClear={() => clearFacet("channels")}/>
                  <MultiSelectFilter label="Abordagem" options={optionsFor("approaches")} selected={facets.approaches.values} exclude={facets.approaches.exclude} onExcludeChange={value => setFacetExclude("approaches", value)} onToggle={value => toggleFacet("approaches", value)} onClear={() => clearFacet("approaches")}/>
                  <MultiSelectFilter label="Tema" options={optionsFor("themeGroups")} selected={facets.themeGroups.values} exclude={facets.themeGroups.exclude} onExcludeChange={value => setFacetExclude("themeGroups", value)} onToggle={value => toggleFacet("themeGroups", value)} onClear={() => clearFacet("themeGroups")}/>
                  <MultiSelectFilter label="Subtema" options={optionsFor("themes")} selected={facets.themes.values} exclude={facets.themes.exclude} onExcludeChange={value => setFacetExclude("themes", value)} onToggle={value => toggleFacet("themes", value)} onClear={() => clearFacet("themes")}/>
                  {/* O valor é o id da marca, e o rótulo vem do
                      servidor — por isso não passa por `humanizeLabel`. */}
                  <MultiSelectFilter label="Marca" options={optionsFor("brands")} selected={facets.brands.values} exclude={facets.brands.exclude} onExcludeChange={value => setFacetExclude("brands", value)} onToggle={value => toggleFacet("brands", value)} onClear={() => clearFacet("brands")}/>
                  {/* Porta-voz é geral de verdade: o matching roda pra
                      toda menção, imprensa e rede — diferente de tier e
                      porte, que são de meio-escopo. */}
                  <MultiSelectFilter
                    label="Porta-voz"
                    unavailable={hasSpokespersons ? undefined : "Esta amostra foi gerada antes da dimensão Porta-voz. Gere uma nova para filtrar por ela."}
                    options={optionsFor("spokespersons")}
                    selected={facets.spokespersons.values}
                    exclude={facets.spokespersons.exclude}
                    onExcludeChange={value => setFacetExclude("spokespersons", value)}
                    onToggle={value => toggleFacet("spokespersons", value)}
                    onClear={() => clearFacet("spokespersons")}
                  />
                </div>
              </div>

              {/* Os grupos de uma fonte só somem quando a barra escolheu
                  a outra — ver `chooseSource`, que limpa o que some. */}
              {sourceView !== "network" && reportHas.press && <div className="filter-group">
                <h4 className="filter-group-title">Imprensa</h4>
                <div className="dashboard-advanced-filters">
                  <MultiSelectFilter label="Tier do veículo" hint="Só imprensa: recorta a imprensa e deixa as menções de redes passarem." options={optionsFor("tiers")} selected={facets.tiers.values} exclude={facets.tiers.exclude} onExcludeChange={value => setFacetExclude("tiers", value)} onToggle={value => toggleFacet("tiers", value)} onClear={() => clearFacet("tiers")}/>
                  <MultiSelectFilter label="Formato" hint="Só imprensa: recorta a imprensa e deixa as menções de redes passarem." options={optionsFor("formats")} selected={facets.formats.values} exclude={facets.formats.exclude} onExcludeChange={value => setFacetExclude("formats", value)} onToggle={value => toggleFacet("formats", value)} onClear={() => clearFacet("formats")}/>
                  {/* Tipo de mídia congelado (2026-11-01). Amostra anterior a
                      2026-10-25 não guardou a coluna: a imprensa dela inteira
                      é "Não informado". */}
                  <MultiSelectFilter label="Tipo de mídia" hint={data?.media_types == null ? NOT_IN_THIS_PHOTO : "Só imprensa: recorta a imprensa e deixa as menções de redes passarem."} options={optionsFor("mediaTypes")} selected={facets.mediaTypes.values} exclude={facets.mediaTypes.exclude} onExcludeChange={value => setFacetExclude("mediaTypes", value)} onToggle={value => toggleFacet("mediaTypes", value)} onClear={() => clearFacet("mediaTypes")}/>
                </div>
                <div className="filter-section">
                  <div className="filter-section-head">
                    <CircleDollarSign size={15} aria-hidden="true"/>
                    <span>Valoração mínima (R$)</span>
                  </div>
                  <input
                    type="number"
                    aria-label="Valoração mínima em reais"
                    min={1}
                    step={1}
                    placeholder="Ex.: 5000"
                    value={minAdValue}
                    onChange={event => setMinAdValue(event.target.value)}
                  />
                  <p className="filter-section-hint">
                    Recorta a imprensa e deixa as menções de redes passarem —
                    valoração é dado de imprensa.
                  </p>
                </div>
              </div>}

              {sourceView !== "press" && reportHas.network && <div className="filter-group">
                <h4 className="filter-group-title">Redes</h4>
                <div className="dashboard-advanced-filters">
                  <MultiSelectFilter label="Porte do publicador" hint="Só redes: recorta as redes e deixa as menções de imprensa passarem." options={optionsFor("influencerTypes")} selected={facets.influencerTypes.values} exclude={facets.influencerTypes.exclude} onExcludeChange={value => setFacetExclude("influencerTypes", value)} onToggle={value => toggleFacet("influencerTypes", value)} onClear={() => clearFacet("influencerTypes")}/>
                  <MultiSelectFilter label="Grupo de publicador" hint="Só redes: recorta as redes e deixa as menções de imprensa passarem." options={optionsFor("publisherGroups")} selected={facets.publisherGroups.values} exclude={facets.publisherGroups.exclude} onExcludeChange={value => setFacetExclude("publisherGroups", value)} onToggle={value => toggleFacet("publisherGroups", value)} onClear={() => clearFacet("publisherGroups")}/>
                </div>
              </div>}
            </div>

            <footer className="drawer-foot">
              <button type="button" className="secondary-button" onClick={resetFilters} disabled={!activeFilterCount}>Limpar filtros</button>
              <button type="button" className="primary-button" onClick={() => { setFiltersOpen(false); filterTriggerRef.current?.focus(); }}><Check size={16} aria-hidden="true"/>{loading ? "Atualizando…" : `Ver ${formatInteger(data?.total_mentions ?? 0)} resultados`}</button>
            </footer>
          </section>
        </div>
      )}

      {loading && !data ? (
        <div className="dashboard-skeleton" role="status" aria-label="Carregando dashboard">
          <span className="sr-only">Carregando dashboard…</span>
          <div className="dashboard-kpi-grid dashboard-kpi-grid--headline" aria-hidden="true">
            {Array.from({ length: 4 }).map((_, index) => <div key={index} className="dashboard-kpi dashboard-skeleton-block"/>)}
          </div>
          <div className="dashboard-section-grid" aria-hidden="true">
            <div className="dashboard-panel dashboard-skeleton-block dashboard-skeleton-panel dashboard-span-8"/>
            <div className="dashboard-panel dashboard-skeleton-block dashboard-skeleton-panel dashboard-span-4"/>
          </div>
        </div>
      ) : data && (
        <>
          {/* Os grandes números (2026-10-30): menções, alcance, interações
              e valoração. Interação é de rede e valoração é de imprensa,
              então cada uma sai com a fonte dela; o detalhe por fonte
              mora nas faixas "Visão geral" da aba. */}
          <section
            className="dashboard-kpi-grid dashboard-kpi-grid--headline"
            style={{ "--kpi-count": 2 + Number(sources.network) + Number(sources.press) + Number(showsCena) } as CSSProperties}
            aria-label="Indicadores principais"
          >
            <MetricCard icon={<MessageSquare size={18}/>} label="Menções" value={formatInteger(data.total_mentions)} context="no recorte selecionado" tone="card1" change={change}/>
            {/* Alcance de rede e de imprensa NUNCA se somam (regras
                diferentes: seguidores × 0,2 por perfil, e a audiência da
                planilha por veículo). Com redes na tela o número é o de
                rede e o de imprensa vai no contexto; só com imprensa, é o
                dela. `press_reach` `null` = não apurado: "—", não 0. */}
            {sources.network ? (
              <MetricCard
                icon={<Users size={18}/>}
                label="Alcance"
                value={formatCompact(data.total_reach)}
                context={
                  sources.press && data.press_reach != null
                    ? `redes · imprensa à parte: ${formatCompact(data.press_reach)}`
                    : "20% dos seguidores, cada perfil uma vez"
                }
                tone="card2"
              />
            ) : (
              <MetricCard
                icon={<Users size={18}/>}
                label="Alcance"
                value={data.press_reach == null ? "—" : formatCompact(data.press_reach)}
                context={
                  data.press_reach == null
                    ? "nenhuma matéria com alcance no recorte"
                    : "imprensa: uma vez por veículo, valor da planilha"
                }
                tone="card2"
              />
            )}
            {showsCena && (
              <MetricCard
                icon={<Scale size={18}/>}
                label="Saldo reputacional"
                value={formatSignedCompact(data.cena_balance)}
                context={`soma do ESCena · imprensa e rede · ${formatInteger(data.total_mentions - data.unscored_count)} com nota`}
                tone="plain"
                title={data.cena_balance == null ? undefined : formatInteger(data.cena_balance)}
              />
            )}
            {sources.network && <MetricCard icon={<Heart size={18}/>} label="Interações" value={formatCompact(data.total_interactions)} context="curtidas, comentários e compartilhamentos" tone="card3"/>}
            {/* Valoração é fato de IMPRENSA: `null` quer dizer "nenhuma
                matéria valorada neste recorte", não "vale R$ 0" — a
                Clipei manda `0,00` de verdade, e o card precisa saber a
                diferença. O tooltip diz a regra de contagem porque este
                é o número que o analista confere contra o Excel da
                clipadora (alcance ninguém confere). */}
            {sources.press && (
              <MetricCard
                icon={<CircleDollarSign size={18}/>}
                label="Valoração"
                value={formatCurrencyCompact(data.total_ad_value)}
                context={
                  data.total_ad_value === null
                    ? "nenhuma matéria valorada no recorte"
                    : "valor publicitário das matérias"
                }
                tone="dark"
                title={
                  data.total_ad_value === null
                    ? undefined
                    : `${formatCurrencyFull(data.total_ad_value)} — matéria que cita N marcas conta N vezes (o alcance, ao contrário, deduplica por perfil)`
                }
              />
            )}
          </section>

          {tab === "overview" && selection && (
            <OverviewTab data={data} token={token} reference={selection} filters={readFilters()} showsCena={showsCena} sources={sources}/>
          )}

          {tab === "themes" && selection && (
            <ThemesTab
              data={data}
              token={token}
              reference={selection}
              facets={facets}
              controls={{ toggle: toggleFacet, setExclude: setFacetExclude, clear: clearFacet, options: optionsFor }}
            >
              <Panel downloadable title="Detalhe por Subtema" subtitle="Por assunto — uma menção em dois subtemas conta nos dois, então as linhas somam mais que o total" className="dashboard-span-12"><BreakdownTable items={data.themes}/></Panel>
            </ThemesTab>
          )}

          {tab === "rankings" && (
            <RankingsTab data={data} showsCena={showsCena} sources={sources}>
              <Panel title="Ranking de Marcas" subtitle="Maior presença no recorte" className="dashboard-span-6" downloadable><DashboardRankedBars items={data.brands} limit={10}/></Panel>
              <Panel title="Conteúdos com Mais Interações" subtitle="Destaques do recorte" className="dashboard-span-6" downloadable>{mentionsRanking(10)}</Panel>
            </RankingsTab>
          )}
        </>
      )}
    </main>
  );
}
